if (!customElements.get('collection-list')) {
  class CollectionList extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        sliderWrapper: '.collection-list__items',
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      };

      this.classes = {
        grid: 'f-grid',
        swiper: 'swiper',
        swiperWrapper: 'swiper-wrapper',
      };

      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`);
      this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);
      this.cardStyle = this.dataset.cardStyle;
      this.itemSize = this.dataset.itemSize;
      this.columnsDesktop = this.dataset.columnsDesktop;
      this.columnsOverride = Number(this.dataset.columnsOverride) || 0;
      this.desktopCardAlignment = this.dataset.desktopCardAlignment || 'center';
      this.mobileSliderEnabled = this.dataset.mobileSlider === 'true';
      this.mobileSliderUntil = this.dataset.mobileSliderUntil || 'mobile';
      this.mobileSlideGap = Number(this.dataset.mobileSlideGap) || 8;
      this.mobileMinSlideWidth = Number(this.dataset.mobileMinSlideWidth) || 110;
      this.tabletMinSlideWidth = Number(this.dataset.tabletMinSlideWidth) || this.mobileMinSlideWidth;
      this.mobileMaxVisibleSlides = Number(this.dataset.mobileMaxVisibleSlides) || 4;
      this.tabletMaxVisibleSlides = Number(this.dataset.tabletMaxVisibleSlides) || this.mobileMaxVisibleSlides;
      this.totalItems = Number(this.dataset.totalItems) || this.querySelectorAll('.swiper-slide').length;

      this.sliderInstance = false;
      this.currentMode = null;

      const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
      mql.onchange = this.init.bind(this);

      const mqlTablet = window.matchMedia(FoxTheme.config.mediaQueryTablet);
      mqlTablet.onchange = this.init.bind(this);

      this.init();
    }

    init() {
      const nextMode = this.getMode();

      if (nextMode === 'none') {
        this.currentMode = nextMode;
        this.destroySlider();
        return;
      }

      if (typeof this.sliderInstance === 'object' && this.currentMode && this.currentMode !== nextMode) {
        this.destroySlider();
      }

      this.currentMode = nextMode;
      this.applyResponsiveLayout();
      this.initSlider();
      this.applyResponsiveLayout();
    }

    initSlider() {
      const getSlidesPerView = (fallbackSlidesPerView) => {
        return this.columnsOverride || fallbackSlidesPerView;
      };

      const desktopSpaceBetween = this.cardStyle == 'card' ? 8 : 20;

      const sliderOptions = {
        modules: [FoxTheme.Swiper.Navigation, FoxTheme.Swiper.Pagination, FoxTheme.Swiper.Mousewheel],
        slidesPerView: 'auto',
        spaceBetween: this.mobileSlideGap,
        watchOverflow: true,
        navigation: {
          nextEl: this.section.querySelector(this.selectors.nextEl),
          prevEl: this.section.querySelector(this.selectors.prevEl),
        },
        pagination: false,
        loop: false,
        threshold: 2,
        mousewheel: {
          enabled: true,
          forceToAxis: true,
        },
        on: {
          init: () => {
            this.applyResponsiveLayout();
          },
          resize: () => {
            this.applyResponsiveLayout();
          },
          breakpoint: () => {
            this.applyResponsiveLayout();
          },
          update: () => {
            this.applyResponsiveLayout();
          },
        },
        breakpoints: {
          768: {
            spaceBetween: this.mobileSliderEnabled ? this.mobileSlideGap : desktopSpaceBetween,
            slidesPerView: this.mobileSliderEnabled
              ? 'auto'
              : getSlidesPerView(this.itemSize == 'fixed' ? (this.columnsDesktop > 4 ? 4 : this.columnsDesktop) : 'auto'),
          },
          1024: {
            spaceBetween: desktopSpaceBetween,
            slidesPerView: getSlidesPerView(
              this.itemSize == 'fixed'
                ? this.columnsDesktop > 6
                  ? this.columnsDesktop - 1
                  : this.columnsDesktop
                : 'auto'
            ),
          },
          1280: {
            spaceBetween: this.cardStyle == 'card' ? 8 : 20,
            slidesPerView: getSlidesPerView(this.itemSize == 'fixed' ? this.columnsDesktop : 'auto'),
          },
        },
      };

      if (typeof this.sliderInstance !== 'object') {
        this.classList.add(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.grid);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);

        this.sliderInstance = new window.FoxTheme.Carousel(this, sliderOptions);
        this.sliderInstance.init();
        this.updateDesktopFitState();

        const focusableElements = FoxTheme.a11y.getFocusableElements(this);

        focusableElements.forEach((element) => {
          element.addEventListener('focusin', () => {
            const slide = element.closest('.swiper-slide');
            this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
          });
        });

        if (Shopify.designMode) {
          document.addEventListener('shopify:block:select', (e) => {
            if (e.detail.sectionId != this.sectionId) return;
            let { target } = e;
            const index = Number(target.dataset.index);

            this.sliderInstance.slider.slideTo(index);
          });
        }
      }
    }

    destroySlider() {
      this.classList.remove(this.classes.swiper);
      this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
      this.sliderWrapper.classList.add(this.classes.grid);
      delete this.dataset.desktopFit;
      delete this.dataset.mobileSliderActive;
      delete this.dataset.mobileTrackFit;
      if (typeof this.sliderInstance !== 'object') return;
      this.sliderInstance.slider.destroy();
      this.sliderInstance = false;
    }

    getMode() {
      const enableSmallScreenSlider =
        this.mobileSliderEnabled &&
        ((this.mobileSliderUntil === 'tablet' && FoxTheme.config.mqlTablet) ||
          (this.mobileSliderUntil !== 'tablet' && FoxTheme.config.mqlMobile));

      if (enableSmallScreenSlider) return 'small-screen';
      if (FoxTheme.config.mqlMobile) return 'none';
      return 'desktop';
    }

    applyResponsiveLayout() {
      if (this.currentMode === 'small-screen') {
        this.prepareSmallScreenLayout();
      } else {
        delete this.dataset.mobileSliderActive;
        delete this.dataset.mobileTrackFit;
      }

      if (typeof this.sliderInstance !== 'object') return;

      if (this.currentMode === 'small-screen') {
        this.updateSmallScreenFitState();
      } else {
        this.updateDesktopFitState();
      }
    }

    prepareSmallScreenLayout() {
      if (this.currentMode !== 'small-screen') return;

      const visibleSlides = this.calculateSmallScreenVisibleSlides();
      this.style.setProperty('--mobile-slide-gap', `${this.mobileSlideGap}px`);

      if (this.dataset.mobileVisibleSlides !== String(visibleSlides)) {
        this.dataset.mobileVisibleSlides = String(visibleSlides);
        this.style.setProperty('--mobile-visible-slides', visibleSlides);

        if (typeof this.sliderInstance === 'object') {
          window.requestAnimationFrame(() => {
            if (typeof this.sliderInstance === 'object' && this.currentMode === 'small-screen') {
              this.sliderInstance.slider.update();
            }
          });
        }
      }

      this.dataset.mobileSliderActive = 'true';
    }

    calculateSmallScreenVisibleSlides() {
      const containerWidth = this.clientWidth || this.offsetWidth || 0;
      const minSlideWidth = FoxTheme.config.mqlMobile ? this.mobileMinSlideWidth : this.tabletMinSlideWidth;
      const maxVisibleSlides = FoxTheme.config.mqlMobile ? this.mobileMaxVisibleSlides : this.tabletMaxVisibleSlides;
      const fitCount = Math.floor((containerWidth + this.mobileSlideGap) / (minSlideWidth + this.mobileSlideGap)) || 1;

      return Math.max(1, Math.min(maxVisibleSlides, fitCount));
    }

    updateSmallScreenFitState() {
      if (typeof this.sliderInstance !== 'object') {
        delete this.dataset.mobileTrackFit;
        return;
      }

      const { slider } = this.sliderInstance;
      if (!slider) {
        delete this.dataset.mobileTrackFit;
        return;
      }

      window.requestAnimationFrame(() => {
        const slides = Array.from(slider.slides || []);
        if (!slides.length) {
          delete this.dataset.mobileTrackFit;
          return;
        }

        const spaceBetween = Number(slider.params.spaceBetween) || this.mobileSlideGap;
        const trackWidth =
          slides.reduce((total, slide) => total + slide.offsetWidth, 0) + Math.max(slides.length - 1, 0) * spaceBetween;
        const containerWidth = this.clientWidth;
        const isUnderfilled = trackWidth <= containerWidth + 1;

        if (isUnderfilled) {
          slider.slideTo(0, 0);
          this.dataset.mobileTrackFit = 'true';
        } else {
          delete this.dataset.mobileTrackFit;
        }
      });
    }

    updateDesktopFitState() {
      if (this.currentMode !== 'desktop' || typeof this.sliderInstance !== 'object') {
        delete this.dataset.desktopFit;
        return;
      }

      const { slider } = this.sliderInstance;
      if (!slider) {
        delete this.dataset.desktopFit;
        return;
      }

      window.requestAnimationFrame(() => {
        const slides = Array.from(slider.slides || []);
        if (!slides.length) {
          delete this.dataset.desktopFit;
          return;
        }

        const spaceBetween = Number(slider.params.spaceBetween) || 0;
        const trackWidth =
          slides.reduce((total, slide) => total + slide.offsetWidth, 0) + Math.max(slides.length - 1, 0) * spaceBetween;
        const containerWidth = this.clientWidth;
        const isUnderfilled = trackWidth <= containerWidth + 1;

        if (isUnderfilled) {
          slider.slideTo(0, 0);
          this.dataset.desktopFit = 'true';
        } else {
          delete this.dataset.desktopFit;
        }
      });
    }
  }
  customElements.define('collection-list', CollectionList);
}
